/**
 * Backend Configuration
 *
 * Configuracao de backend Python/FastAPI.
 * O frontend React comunica EXCLUSIVAMENTE com a API Python.
 *
 * @since 2025-12-15
 * @epic INIT-0046
 */

// URLs base do backend Python
// Em desenvolvimento, usamos '' (vazio) para usar o proxy do Vite
// Em producao, pode ser configurado via VITE_PYTHON_API_URL
const BACKEND_URL = import.meta.env.VITE_PYTHON_API_URL || '';

// Prefixo de API Python
const API_PREFIX = '/api/v1';

/**
 * Obtem a URL base do backend
 */
export function getBackendUrl(): string {
  return BACKEND_URL;
}

/**
 * Obtem o prefixo de API
 */
export function getApiPrefix(): string {
  return API_PREFIX;
}

/**
 * Constroi URL completa da API
 */
export function buildApiUrl(path: string): string {
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `${BACKEND_URL}${API_PREFIX}${cleanPath}`;
}

/**
 * @deprecated Sempre retorna false - usar apenas Python
 * Mantida para compatibilidade com código legado
 */
export function isYiiBackend(): boolean {
  return false;
}

/**
 * Verifica se esta usando o backend Python
 */
export function isPythonBackend(): boolean {
  return true;
}

/**
 * Configuracao do backend Python/FastAPI
 */
export const backendConfig = {
  // Python/FastAPI nao usa CSRF (usa JWT)
  useCsrf: false,
  csrfHeaderName: '',
  csrfCookieName: '',
  // Python retorna dados diretamente
  responseWrapper: null,
  // Autenticacao via JWT Bearer token
  authType: 'jwt' as const,
};

/**
 * Obtem configuracao do backend
 */
export function getCurrentBackendConfig() {
  return backendConfig;
}

/**
 * Endpoints mapeados por modulo
 *
 * Python FastAPI structure:
 * - /api/v1/person/persons - Person CRUD
 * - /api/v1/core/auth - Authentication
 * - /api/v1/content/contents - Content CMS
 * - /api/v1/media/upload - Media upload
 * - /api/v1/resource/resources - Resource CRUD
 * - /api/v1/product/products - Product CRUD
 */
export const endpoints = {
  person: {
    list: () => '/person/persons',
    get: (id: number) => `/person/persons/${id}`,
    create: () => '/person/persons',
    update: (id: number) => `/person/persons/${id}`,
    delete: (id: number) => `/person/persons/${id}`,
    search: () => '/person/persons/search',
    contacts: (id: number) => `/person/persons/${id}/contacts`,
    // Tipos de atributos de pessoa
    attributeTypes: () => '/person/person-attribute-types',
    attributeType: (id: number) => `/person/person-attribute-types/${id}`,
    attributeTypeByCode: (code: string) => `/person/person-attribute-types/code/${code}`,
    // Atributos de pessoa (definições)
    attributes: () => '/person/person-attributes',
    attribute: (id: number) => `/person/person-attributes/${id}`,
    // Valores de atributos EAV de uma pessoa (preencher/editar/histórico) [TMF9-58246]
    attributeValueDefinitions: (personTypeId: number) =>
      `/person/person-attribute-values/definitions/${personTypeId}`,
    attributeValues: (personId: number) => `/person/person-attribute-values/person/${personId}`,
    attributeValue: (personId: number, attributeCode: string) =>
      `/person/person-attribute-values/person/${personId}/${attributeCode}`,
    attributeValuesBulk: (personId: number) =>
      `/person/person-attribute-values/person/${personId}/bulk`,
    attributeValueHistory: (personId: number, attributeCode: string) =>
      `/person/person-attribute-values/person/${personId}/${attributeCode}/history`,
  },
  // Product Module - tmf_product
  product: {
    // Products
    list: () => '/product/products',
    get: (id: number) => `/product/products/${id}`,
    create: () => '/product/products',
    update: (id: number) => `/product/products/${id}`,
    delete: (id: number) => `/product/products/${id}`,
    byCode: (code: string) => `/product/products/code/${code}`,
    byEan: (ean: string) => `/product/products/ean/${ean}`,
    validateEan: (ean: string) => `/product/products/validate/ean/${ean}`,
    // Categories
    categories: {
      list: () => '/product/categories',
      get: (id: number) => `/product/categories/${id}`,
      create: () => '/product/categories',
      update: (id: number) => `/product/categories/${id}`,
      delete: (id: number) => `/product/categories/${id}`,
      tree: () => '/product/categories/tree',
      roots: () => '/product/categories/roots',
      children: (id: number) => `/product/categories/${id}/children`,
      ancestors: (id: number) => `/product/categories/${id}/ancestors`,
      descendants: (id: number) => `/product/categories/${id}/descendants`,
      move: (id: number) => `/product/categories/${id}/move`,
      reorder: (id: number) => `/product/categories/${id}/reorder`,
    },
    // Brands
    brands: {
      list: () => '/product/brands',
      get: (id: number) => `/product/brands/${id}`,
      create: () => '/product/brands',
      update: (id: number) => `/product/brands/${id}`,
      delete: (id: number) => `/product/brands/${id}`,
    },
    // Families
    families: {
      list: () => '/product/families',
      get: (id: number) => `/product/families/${id}`,
      create: () => '/product/families',
      update: (id: number) => `/product/families/${id}`,
      delete: (id: number) => `/product/families/${id}`,
    },
    // Types
    types: {
      list: () => '/product/types',
      get: (id: number) => `/product/types/${id}`,
      create: () => '/product/types',
      update: (id: number) => `/product/types/${id}`,
      delete: (id: number) => `/product/types/${id}`,
    },
    // Inventory
    inventory: {
      stock: (productId: number) => `/product/inventory/stock/${productId}`,
      stockByWarehouse: (productId: number) => `/product/inventory/stock/${productId}/by-warehouse`,
      credit: () => '/product/inventory/credit',
      debit: () => '/product/inventory/debit',
      reserve: () => '/product/inventory/reserve',
      releaseReservation: () => '/product/inventory/release-reservation',
      confirmReservation: () => '/product/inventory/confirm-reservation',
      transfer: () => '/product/inventory/transfer',
      history: (productId: number) => `/product/inventory/history/${productId}`,
      lowStock: () => '/product/inventory/low-stock',
      stats: () => '/product/inventory/stats',
    },
    // Warehouses
    warehouses: {
      list: () => '/product/warehouses',
      get: (id: number) => `/product/warehouses/${id}`,
      create: () => '/product/warehouses',
      update: (id: number) => `/product/warehouses/${id}`,
      delete: (id: number) => `/product/warehouses/${id}`,
      roots: () => '/product/warehouses/roots',
      children: (id: number) => `/product/warehouses/${id}/children`,
      summary: (id: number) => `/product/warehouses/${id}/summary`,
      getConfig: (id: number, key: string) => `/product/warehouses/${id}/config/${key}`,
      setConfig: (id: number, key: string) => `/product/warehouses/${id}/config/${key}`,
    },
    // Images
    images: {
      list: () => '/product/images',
      get: (id: number) => `/product/images/${id}`,
      update: (id: number) => `/product/images/${id}`,
      delete: (id: number) => `/product/images/${id}`,
      byProduct: (productId: number) => `/product/images/product/${productId}`,
      setPrimary: (id: number) => `/product/images/${id}/primary`,
      upload: (productId: number) => `/product/images/upload/${productId}`,
    },
    // Health
    health: () => '/product/health',
    info: () => '/product/info',
    stats: () => '/product/stats',
  },
  resource: {
    list: () => '/resource/resources',
    get: (id: number) => `/resource/resources/${id}`,
    create: () => '/resource/resources',
    update: (id: number) => `/resource/resources/${id}`,
    delete: (id: number) => `/resource/resources/${id}`,
  },
  // Content Module - tmf_content (CMS)
  content: {
    // CRUD principal
    list: () => '/content/contents',
    get: (id: number) => `/content/contents/${id}`,
    getByCode: (code: string) => `/content/contents/code/${code}`,
    create: () => '/content/contents',
    update: (id: number) => `/content/contents/${id}`,
    delete: (id: number) => `/content/contents/${id}`,
    // Publicacao/Arquivamento
    publish: (id: number) => `/content/contents/${id}/publish`,
    unpublish: (id: number) => `/content/contents/${id}/unpublish`,
    archive: (id: number) => `/content/contents/${id}/archive`,
    restore: (id: number) => `/content/contents/${id}/restore`,
    duplicate: (id: number) => `/content/contents/${id}/duplicate`,
    // Busca
    search: () => '/content/contents/search',
    autocomplete: () => '/content/contents/autocomplete',
    related: (id: number) => `/content/contents/${id}/related`,
    // I18n (traducoes)
    i18n: (id: number) => `/content/contents/${id}/i18n`,
    // Bulk operations
    bulkPublish: () => '/content/contents/bulk/publish',
    bulkArchive: () => '/content/contents/bulk/archive',
    bulkDelete: () => '/content/contents/bulk/delete',
    // Categorias
    categories: () => '/content/categories',
    category: (id: number) => `/content/categories/${id}`,
    // Menus
    menus: () => '/content/menus',
    menu: (id: number) => `/content/menus/${id}`,
    // Visibilidade
    visibility: (contentId: number) => `/content/contents/${contentId}/visibility`,
    // Verificação de visibilidade por contexto (role)
    visibilityCheck: () => '/content/visibility/check',
    visibilityByContext: () => '/content/visibility/by-context',
    visibilityCodesByContext: () => '/content/visibility/codes-by-context',
    // SEO
    seo: (contentId: number) => `/content/contents/${contentId}/seo`,
    // Imagens
    images: (contentId: number) => `/content/contents/${contentId}/images`,
  },
  auth: {
    login: () => '/core/auth/login',
    logout: () => '/core/auth/logout',
    me: () => '/core/auth/me',
    refresh: () => '/core/auth/refresh',
  },
  // Ranking Module - tmf_ranking
  ranking: {
    // CRUD
    list: () => '/ranking',
    get: (id: number) => `/ranking/${id}`,
    create: () => '/ranking',
    update: (id: number) => `/ranking/${id}`,
    delete: (id: number) => `/ranking/${id}`,
    // Leaderboard e Resultados
    leaderboard: (id: number) => `/ranking/${id}/leaderboard`,
    results: (id: number) => `/ranking/${id}/results`,
    statistics: (id: number) => `/ranking/${id}/statistics`,
    // Geracao
    generate: (id: number) => `/ranking/${id}/generate`,
    // Jobs: rota flat '/ranking/jobs' consumida via `@/services/rankingJobsApi`
    // (service dedicado e unico, TMF9-58240) — nao duplicada aqui.
    // Premiacao por posicao (TMF9-58344)
    prizes: {
      list: (rankingId: number) => `/ranking/${rankingId}/prizes`,
      create: (rankingId: number) => `/ranking/${rankingId}/prizes`,
      update: (rankingId: number, prizeId: number) =>
        `/ranking/${rankingId}/prizes/${prizeId}`,
      delete: (rankingId: number, prizeId: number) =>
        `/ranking/${rankingId}/prizes/${prizeId}`,
    },
  },
  // Media Module - tmf_media
  media: {
    // Image proxy
    imageProxy: (path: string) => `/media/image-proxy?path=${encodeURIComponent(path)}`,
    // Upload endpoints
    upload: () => '/media/upload',
    uploadImage: () => '/media/upload/image',
    uploadVideo: () => '/media/upload/video',
    uploadAudio: () => '/media/upload/audio',
    validate: () => '/media/upload/validate',
    formats: () => '/media/upload/formats',
    // Media files (File Manager)
    files: () => '/media/files',
    list: () => '/media/files',
    get: (id: number) => `/media/files/${id}`,
    delete: (id: number) => `/media/files/${id}`,
    url: (id: number) => `/media/files/${id}/url`,
  },
  // Docs Module - tmf_docs (Markdown files from docs/)
  docs: {
    list: () => '/docs/files',
    get: (fileId: string) => `/docs/files/${fileId}`,
    create: () => '/docs/files',
    update: (fileId: string) => `/docs/files/${fileId}`,
    delete: (fileId: string) => `/docs/files/${fileId}`,
    folders: () => '/docs/folders',
  },
  // Segmentation Module - tmf_segmentation (Regions, Segments)
  segmentation: {
    // Regions
    regions: {
      list: () => '/segmentation/v1/regions',
      get: (id: number) => `/segmentation/v1/regions/${id}`,
      tree: () => '/segmentation/v1/regions/tree',
      children: (id: number) => `/segmentation/v1/regions/${id}/children`,
      persons: (regionId: number) => `/segmentation/v1/regions/${regionId}/persons`,
    },
    // Segments
    segments: {
      list: () => '/segmentation/v1/segments',
      get: (id: number) => `/segmentation/v1/segments/${id}`,
      tree: () => '/segmentation/v1/segments/tree',
      children: (id: number) => `/segmentation/v1/segments/${id}/children`,
      persons: (segmentId: number) => `/segmentation/v1/segments/${segmentId}/persons`,
    },
    // Person associations
    personRegions: (personId: number) => `/segmentation/v1/persons/${personId}/regions`,
    personSegments: (personId: number) => `/segmentation/v1/persons/${personId}/segments`,
  },
  // Access Module - Perfis de Acesso (auth_item, auth_assignment)
  access: {
    profiles: () => '/person/access/profiles',
    personProfile: (personId: number) => `/person/access/profiles/persons/${personId}`,
    // Login Logs
    loginLogs: () => '/person/access/login-logs',
    loginLogActions: () => '/person/access/login-logs/actions',
  },
  // Organization Module - Client Groups, Hierarchical Organization
  organization: {
    // Client Groups
    clientGroups: {
      list: () => '/person/client-groups/',
      get: (id: number) => `/person/client-groups/${id}`,
      getByCode: (code: string) => `/person/client-groups/code/${code}`,
      create: () => '/person/client-groups/',
      update: (id: number) => `/person/client-groups/${id}`,
      delete: (id: number) => `/person/client-groups/${id}`,
      // Client management within groups
      clients: (groupId: number) => `/person/client-groups/${groupId}/clients`,
      addClients: (groupId: number) => `/person/client-groups/${groupId}/clients`,
      removeClient: (groupId: number, clientId: number) => `/person/client-groups/${groupId}/clients/${clientId}`,
      updateClient: (groupId: number, clientId: number) => `/person/client-groups/${groupId}/clients/${clientId}`,
    },
    // Client search for selection
    clientSearch: () => '/person/clients/search',
    // Hierarchical Levels
    hierarchicalLevels: {
      list: () => '/person/hierarchical-levels/',
      get: (id: number) => `/person/hierarchical-levels/${id}`,
    },
    // Hierarchical Organizations
    hierarchicalOrg: {
      list: () => '/person/hierarchical-org/',
      get: (id: number) => `/person/hierarchical-org/${id}`,
      tree: () => '/person/hierarchical-org/tree',
    },
    // Business Areas
    businessAreas: {
      list: () => '/person/business-areas/',
      get: (id: number) => `/person/business-areas/${id}`,
      tree: () => '/person/business-areas/tree',
    },
  },
  // Corp Structure Module - Corporation Hierarchical Structure
  corpStructure: {
    list: () => '/person/corp-structure/',
    create: () => '/person/corp-structure/',
    delete: (id: number) => `/person/corp-structure/${id}`,
  },
  // Hierarchy Module - Person Hierarchy, Corporations Served
  hierarchy: {
    // Person Hierarchy
    getHierarchy: (personId: number) => `/person/person/${personId}/hierarchy`,
    updateHierarchy: (personId: number) => `/person/person/${personId}/hierarchy`,
    searchSuperiors: (personId: number) => `/person/person/${personId}/hierarchy/superior/search`,
    getSubordinates: (personId: number) => `/person/person/${personId}/hierarchy/subordinates`,
    // Corporation hierarchy (employees of a corporation)
    getCorporationHierarchy: (corporationId: number) => `/person/corporation/${corporationId}/hierarchy`,
    // Corporations Served
    corporationsServed: {
      list: (personId: number) => `/person/person/${personId}/corporations-served`,
      add: (personId: number) => `/person/person/${personId}/corporations-served`,
      update: (personId: number, recordId: number) => `/person/person/${personId}/corporations-served/${recordId}`,
      remove: (personId: number, recordId: number) => `/person/person/${personId}/corporations-served/${recordId}`,
    },
    // Corporation search for selection
    corporationSearch: () => '/person/corporations/search',
  },
  // Integration Module - System Integrations
  integration: {
    // System Integrations (sistemas externos como SAP, TOTVS, CRM)
    systems: {
      list: () => '/person/system-integrations',
      get: (id: number) => `/person/system-integrations/${id}`,
      getByCode: (code: string) => `/person/system-integrations/code/${code}`,
      create: () => '/person/system-integrations',
      update: (id: number) => `/person/system-integrations/${id}`,
      delete: (id: number) => `/person/system-integrations/${id}`,
      test: (id: number) => `/person/system-integrations/${id}/test`,
    },
    // Person System Integrations (vinculação pessoa-sistema)
    personIntegrations: {
      list: (personId: number) => `/person/person-integrations/person/${personId}`,
      get: (id: number) => `/person/person-integrations/${id}`,
      getByExternal: (systemCode: string, externalId: string) =>
        `/person/person-integrations/external/${systemCode}/${externalId}`,
      create: () => '/person/person-integrations',
      update: (id: number) => `/person/person-integrations/${id}`,
      delete: (id: number) => `/person/person-integrations/${id}`,
      sync: (id: number) => `/person/person-integrations/${id}/sync`,
      bulkSync: () => '/person/person-integrations/bulk-sync',
    },
  },
};

export default {
  getBackendUrl,
  getApiPrefix,
  buildApiUrl,
  isYiiBackend,
  isPythonBackend,
  getCurrentBackendConfig,
  endpoints,
};
